import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';

/**
 * Global Error Boundary Component
 * Story 11.3: Error Handling and User-Friendly Error Messages
 * AC-11.3.7: Global Error Boundary
 *
 * Catches React component failures and displays user-friendly fallback UI
 * with recovery options (refresh, go home).
 */

interface Props {
  children: ReactNode;
  /** Custom fallback component */
  fallback?: ReactNode;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Log error to console (dev) and prepare for production monitoring
 * This function is Sentry-ready - add Sentry.captureException when configured
 */
function logErrorToService(error: Error, errorInfo: ErrorInfo): void {
  // Development logging
  if (import.meta.env.DEV) {
    console.group('ErrorBoundary caught an error');
    console.error('Error:', error);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();
  }

  // Production-ready: Add monitoring service integration here
  // Example with Sentry:
  // if (typeof Sentry !== 'undefined') {
  //   Sentry.captureException(error, {
  //     extra: { componentStack: errorInfo.componentStack },
  //   });
  // }
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Store error info for display
    this.setState({ errorInfo });

    // Log error to monitoring service
    logErrorToService(error, errorInfo);

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-dvh items-center justify-center bg-canvas px-4 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-md space-y-8">
            <div className="text-center">
              {/* Error icon */}
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-danger-wash">
                <svg
                  className="h-8 w-8 text-danger"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              {/* Error message - user-friendly, no technical details */}
              <h1 className="mb-2 text-2xl font-bold text-ink">
                Something went wrong
              </h1>
              <p className="mb-6 text-ink-muted">
                We're sorry for the inconvenience. Please try refreshing the page
                or return to the home page.
              </p>

              {/* Recovery actions */}
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={this.handleReload}
                  className="px-6 py-3 text-base font-medium"
                >
                  <svg
                    className="-ml-1 mr-2 size-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Refresh Page
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={this.handleGoHome}
                  className="px-6 py-3 text-base font-medium"
                >
                  <svg
                    className="-ml-1 mr-2 size-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  Go Home
                </Button>
              </div>

              {/* Error details - only in development */}
              {import.meta.env.DEV && this.state.error && (
                <details className="mt-8 text-left">
                  <summary className="cursor-pointer text-sm text-ink-muted hover:text-ink">
                    Technical details (dev only)
                  </summary>
                  <div className="mt-2 rounded-md bg-hover p-4">
                    <p className="mb-2 font-mono text-sm text-danger">
                      {this.state.error.message}
                    </p>
                    {this.state.errorInfo && (
                      <pre className="max-h-40 overflow-auto text-xs text-ink-muted-on-wash">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
